import React from 'react'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { AuthorizationService } from '@hapana/shared/services/authorization.service'
import type { ComputedPermissions } from '@hapana/shared/schemas/users/staff-user-schemas'
import type { AccessPermissionsKey } from '@hapana/shared/schemas/users/enums/access-permissions'
import type { CorporatePermissionsKey } from '@hapana/shared/schemas/users/enums/corporate-permissions'
import type { SitePermissionsKey } from '@hapana/shared/schemas/users/enums/site-permissions'
import type {
  IAccessPolicyPermissions,
  StaffUserTypePermissions,
} from '@hapana/shared/services/authorization.service'
import { useMe } from '_hooks/me.hooks'
import useSiteStore from '_stores/site.store'

export type PermissionKey = AccessPermissionsKey | CorporatePermissionsKey | SitePermissionsKey

interface PermissionsStoreState {
  parsedPermissions: ComputedPermissions | null
  rawPermissions: string | null
  currentSiteId: string | null
  isLoading: boolean
  error: string | null
}

const initialState: PermissionsStoreState = {
  parsedPermissions: null,
  rawPermissions: null,
  currentSiteId: null,
  isLoading: false,
  error: null,
}

interface PermissionsStoreActions {
  hasPermission: (permissionKey: PermissionKey) => boolean

  hasAnyPermission: (permissionKeys: PermissionKey[]) => boolean

  hasAllPermissions: (permissionKeys: PermissionKey[]) => boolean

  getPermissionsForSite: (siteId: string) => string | null

  getAllPermissionsForSite: (siteId: string) => string[]

  getAccessibleSites: () => string[]

  getSiteIdsWithAccess: (permissionsToFilterBy?: IAccessPolicyPermissions) => {
    siteIds: string[]
    isAllSites: boolean
  }

  hasUniversalSiteAccess: () => boolean

  hasPermissions: (permissions: StaffUserTypePermissions, siteId?: string) => boolean

  reset: () => void
}

type PermissionsStore = PermissionsStoreState & PermissionsStoreActions

const usePermissionsStore = create<PermissionsStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    hasPermission: (permissionKey: PermissionKey) => {
      const { parsedPermissions, currentSiteId } = get()

      if (!parsedPermissions) {
        return false
      }

      try {
        return AuthorizationService.hasPermission(
          parsedPermissions,
          permissionKey,
          currentSiteId || undefined
        )
      } catch (_) {
        return false
      }
    },

    hasAnyPermission: (permissionKeys: PermissionKey[]) => {
      return permissionKeys.some((key) => get().hasPermission(key))
    },

    hasAllPermissions: (permissionKeys: PermissionKey[]) => {
      return permissionKeys.every((key) => get().hasPermission(key))
    },

    getPermissionsForSite: (siteId: string) => {
      const { parsedPermissions } = get()

      if (!parsedPermissions) {
        return null
      }

      return parsedPermissions.sites?.[siteId] || parsedPermissions.sites?.['*'] || null
    },

    getAllPermissionsForSite: (siteId: string) => {
      const { parsedPermissions } = get()
      if (!parsedPermissions) {
        return []
      }
      return AuthorizationService.getPermissionsForSite(parsedPermissions, siteId)
    },

    getAccessibleSites: () => {
      const { parsedPermissions } = get()
      if (!parsedPermissions?.sites) {
        return []
      }
      if (parsedPermissions.sites['*']) {
        return ['*']
      }
      return Object.keys(parsedPermissions.sites)
    },

    getSiteIdsWithAccess: (permissionsToFilterBy?: IAccessPolicyPermissions) => {
      const { parsedPermissions } = get()
      if (!parsedPermissions) {
        return { siteIds: [], isAllSites: false }
      }
      const userContext = {
        id: 'current-user',
        type: 'staff' as const,
        permissions: parsedPermissions,
      }

      return AuthorizationService.STAFF_USER.siteIdsWithAccess(userContext, permissionsToFilterBy)
    },

    hasUniversalSiteAccess: () => {
      const { parsedPermissions } = get()
      return !!parsedPermissions?.sites?.['*']
    },

    hasPermissions: (permissions: StaffUserTypePermissions, siteId?: string) => {
      const { parsedPermissions, currentSiteId } = get()
      if (!parsedPermissions) {
        return false
      }
      const userContext = {
        id: 'current-user',
        type: 'staff' as const,
        permissions: parsedPermissions,
      }

      return AuthorizationService.STAFF_USER.hasPermissions(
        userContext,
        permissions,
        siteId || currentSiteId || undefined
      )
    },

    reset: () => set(initialState),
  }))
)

export const usePermissions = () => {
  const { data: meData, isLoading: isMeLoading, error: meError } = useMe()
  const { getCurrentSiteId } = useSiteStore()
  const currentSiteId = getCurrentSiteId()

  const store = usePermissionsStore()

  React.useEffect(() => {
    const current = usePermissionsStore.getState()
    const newRaw = meData?.me?.staffUserPermissions

    if (newRaw) {
      // Skip if the raw permissions string hasn't changed and we're already done loading
      if (newRaw === current.rawPermissions && !current.isLoading) return
      try {
        const parsedPermissions: ComputedPermissions = JSON.parse(newRaw)
        usePermissionsStore.setState({
          parsedPermissions,
          rawPermissions: newRaw,
          isLoading: false,
          error: null,
        })
      } catch (error) {
        usePermissionsStore.setState({
          parsedPermissions: null,
          rawPermissions: newRaw,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    } else if (!isMeLoading) {
      // Only clear permissions if we never had any — if we previously had valid
      // permissions, preserve them through transient meData gaps (token refresh,
      // network stall) to avoid the "No locations" flicker.
      if (current.parsedPermissions !== null) return
      usePermissionsStore.setState({
        parsedPermissions: null,
        rawPermissions: null,
        isLoading: false,
        error: null,
      })
    } else if (isMeLoading) {
      if (current.isLoading) return
      usePermissionsStore.setState({ isLoading: true })
    }
  }, [meData, isMeLoading])

  React.useEffect(() => {
    if (usePermissionsStore.getState().currentSiteId === currentSiteId) return
    usePermissionsStore.setState({ currentSiteId })
  }, [currentSiteId])

  const isLoading = isMeLoading || store.isLoading
  const error = meError || store.error

  return {
    ...store,
    isLoading,
    error,
    meData,
    rawPermissions: meData?.me?.staffUserPermissions || null,
  }
}

export default usePermissionsStore
